import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MediaBannerTab from '@/components/staff/MediaBannerTab';
import { toast } from 'sonner';
import { PlusCircle } from 'lucide-react';

const TYPES = ['SUBSCRIPTION_ACTIVATED', 'LOW_BALANCE', 'EXPIRING_SOON', 'EXPIRED', 'PAYMENT_RECEIVED', 'PROMOTIONAL'];

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(TYPES[0]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => staffApi.get('/notifications').then(({ data }) => setNotifications(data.notifications));

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await staffApi.post('/notifications', { type, title, message });
      toast.success('Notification logged');
      setOpen(false);
      setTitle('');
      setMessage('');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tabs defaultValue="log" className="space-y-4">
      <TabsList>
        <TabsTrigger value="log" data-testid="notif-log-tab">Notifications</TabsTrigger>
        <TabsTrigger value="media" data-testid="notif-media-tab">Media</TabsTrigger>
      </TabsList>
      <TabsContent value="log" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-700 text-charcoal">Notifications</h1>
          <p className="text-sm text-muted-ink">
            In-app log only for now — no email/SMS/push provider is connected yet.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="notification-create-btn" className="bg-saffron hover:bg-saffron-hover">
              <PlusCircle size={16} className="mr-2" /> New Notification
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="notification-form-dialog">
            <DialogHeader><DialogTitle>New Notification</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="notification-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input data-testid="notification-title-input" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea data-testid="notification-message-input" value={message} onChange={(e) => setMessage(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="notification-submit-btn" disabled={busy || !title || !message} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
                {busy ? 'Saving…' : 'Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-border/40 divide-y divide-border/30">
        {notifications.map((n) => (
          <div key={n.id} className="p-4" data-testid="notification-row">
            <div className="flex justify-between">
              <p className="font-medium text-charcoal">{n.title}</p>
              <span className="text-xs text-muted-ink">{n.type.replace(/_/g, ' ')}</span>
            </div>
            <p className="text-sm text-muted-ink mt-1">{n.message}</p>
          </div>
        ))}
        {notifications.length === 0 && <p className="p-6 text-sm text-muted-ink text-center">No notifications yet.</p>}
      </div>
      </TabsContent>
      <TabsContent value="media"><MediaBannerTab /></TabsContent>
    </Tabs>
  );
}
